import { useEffect } from "react";
import { Link } from "react-router-dom";

const PrivacyPolicyPage = () => {
  useEffect(() => {
    document.title = "Privacy Policy | Mayker Financial Group";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF8F3] pt-24 pb-20" data-testid="privacy-policy-page">
      <div className="max-w-3xl mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="mb-12 border-b border-[#DDD8CC] pb-8">
          <p className="text-xs font-medium tracking-[0.2em] uppercase text-[#C9A84C] mb-3">
            Mayker Financial Group Inc.
          </p>
          <h1 className="font-playfair text-4xl lg:text-5xl font-bold text-[#0C1B33] mb-4">
            Privacy Policy
          </h1>
          <p className="text-sm text-[#6B7A99]">Effective date: June 11, 2026</p>
        </div>

        <div className="space-y-10 text-[#1A2540]">
          <p className="text-base leading-relaxed text-[#3A4663]">
            {`Mayker Financial Group Inc. ("Mayker," "we," "us") is an independent insurance and financial services brokerage based in Victoria, British Columbia. We are committed to protecting your personal information in accordance with the Personal Information Protection and Electronic Documents Act (PIPEDA) and British Columbia's Personal Information Protection Act (PIPA).`}
          </p>

          <section data-testid="privacy-section-collect">
            <h2 className="font-playfair text-2xl font-semibold text-[#0C1B33] mb-4">What we collect</h2>
            <p className="text-base leading-relaxed text-[#3A4663] mb-4">
              We collect personal information you provide to us directly, including:
            </p>
            <ul className="space-y-3 mb-4">
              <li className="flex gap-3 text-base leading-relaxed text-[#3A4663]">
                <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#C9A84C] flex-shrink-0" />
                <span>Contact details: name, email address, phone number, and mailing address</span>
              </li>
              <li className="flex gap-3 text-base leading-relaxed text-[#3A4663]">
                <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#C9A84C] flex-shrink-0" />
                <span>{`Information you share when requesting a quote or consultation, such as your family situation, homeownership or mortgage status, and what's prompting you to look into coverage`}</span>
              </li>
              <li className="flex gap-3 text-base leading-relaxed text-[#3A4663]">
                <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#C9A84C] flex-shrink-0" />
                <span>Information required to apply for insurance or financial products, which may include date of birth, financial details, and health information</span>
              </li>
            </ul>
            <p className="text-base leading-relaxed text-[#3A4663]">
              We collect this information when you fill out a form on our website or
              on social media (including Facebook and Instagram lead forms), book a
              meeting, contact us by phone or email, or work with us as a client.
            </p>
          </section>

          <section data-testid="privacy-section-use">
            <h2 className="font-playfair text-2xl font-semibold text-[#0C1B33] mb-4">How we use it</h2>
            <p className="text-base leading-relaxed text-[#3A4663] mb-4">
              We use your personal information to:
            </p>
            <ul className="space-y-3 mb-4">
              {[
                "Respond to your inquiry and provide quotes or advice you've requested",
                "Assess needs and recommend suitable insurance or financial products",
                "Submit applications to insurers and financial institutions on your behalf, with your consent",
                "Service your existing policies and accounts",
                "Communicate with you about your file, and—where you've consented—send you relevant updates",
                "Meet legal, regulatory, and licensing obligations",
              ].map((item, i) => (
                <li key={i} className="flex gap-3 text-base leading-relaxed text-[#3A4663]">
                  <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#C9A84C] flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-base leading-relaxed text-[#3A4663] font-medium">
              We do not sell your personal information.
            </p>
          </section>

          <section data-testid="privacy-section-leadads">
            <h2 className="font-playfair text-2xl font-semibold text-[#0C1B33] mb-4">Lead ads and social media forms</h2>
            <p className="text-base leading-relaxed text-[#3A4663]">
              {`If you submit your information through a lead form on Facebook or Instagram, Meta provides us with the details you entered (such as your name, email, and phone number) along with your answers to the form questions. We use that information solely to follow up on your request. Meta's own handling of your data is governed by Meta's Privacy Policy.`}
            </p>
          </section>

          <section data-testid="privacy-section-share">
            <h2 className="font-playfair text-2xl font-semibold text-[#0C1B33] mb-4">When we share information</h2>
            <p className="text-base leading-relaxed text-[#3A4663] mb-4">
              We share personal information only as needed to serve you:
            </p>
            <ul className="space-y-3">
              {[
                "With insurers and financial institutions to obtain quotes and process applications, with your consent",
                "With service providers who help us operate (such as scheduling, customer relationship, and email tools), under appropriate safeguards",
                "Where required by law or by our regulators",
              ].map((item, i) => (
                <li key={i} className="flex gap-3 text-base leading-relaxed text-[#3A4663]">
                  <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#C9A84C] flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          <section data-testid="privacy-section-consent">
            <h2 className="font-playfair text-2xl font-semibold text-[#0C1B33] mb-4">Consent</h2>
            <p className="text-base leading-relaxed text-[#3A4663]">
              By providing your information, you consent to its collection, use, and
              disclosure as described in this policy. You may withdraw consent at any
              time, subject to legal and contractual restrictions, by contacting us.
              Withdrawing consent may limit our ability to serve you.
            </p>
          </section>

          <section data-testid="privacy-section-safeguards">
            <h2 className="font-playfair text-2xl font-semibold text-[#0C1B33] mb-4">Safeguards and retention</h2>
            <p className="text-base leading-relaxed text-[#3A4663]">
              We protect personal information with physical, organizational, and
              technological safeguards appropriate to its sensitivity. We retain
              information only as long as needed for the purposes described above and
              to meet legal and regulatory requirements, after which it is securely
              destroyed.
            </p>
          </section>

          <section data-testid="privacy-section-cookies">
            <h2 className="font-playfair text-2xl font-semibold text-[#0C1B33] mb-4">Cookies and analytics</h2>
            <p className="text-base leading-relaxed text-[#3A4663]">
              Our website may use cookies and similar technologies (including
              analytics and advertising pixels such as the Meta Pixel) to understand
              how visitors use our site and to measure advertising. You can manage
              cookies through your browser settings.
            </p>
          </section>

          <section data-testid="privacy-section-rights">
            <h2 className="font-playfair text-2xl font-semibold text-[#0C1B33] mb-4">Your rights</h2>
            <p className="text-base leading-relaxed text-[#3A4663]">
              {`You may request access to the personal information we hold about you, ask us to correct it, or ask questions about how it's handled. We will respond within the timelines required by law.`}
            </p>
          </section>

          <section data-testid="privacy-section-contact">
            <h2 className="font-playfair text-2xl font-semibold text-[#0C1B33] mb-4">Contact us</h2>
            <div className="bg-white border border-[#DDD8CC] rounded-lg p-6 text-base leading-relaxed text-[#3A4663]">
              <p className="font-semibold text-[#0C1B33]">Privacy Officer</p>
              <p>Mayker Financial Group Inc.</p>
              <p>Victoria, British Columbia</p>
              <p>
                Phone:{" "}
                <a href="tel:7788361962" className="text-[#B08F3A] hover:underline">778-836-1962</a>
              </p>
              <p>
                Web:{" "}
                <Link to="/contact" className="text-[#B08F3A] hover:underline">maykerfinancial.com/contact</Link>
              </p>
            </div>
            <p className="text-base leading-relaxed text-[#3A4663] mt-4">
              If you are not satisfied with our response, you may contact the Office
              of the Privacy Commissioner of Canada or the Office of the Information
              and Privacy Commissioner for British Columbia.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
